import styles from "./DisclosureNote.module.css";
import type { RankingDisclosure } from "../api/types";

/**
 * How a ranking was decided, in core's own words.
 *
 * Canon § 12 requires bid-driven ranking to be visibly described as sponsored placement, and
 * `contracts/API.md` § Ranking disclosure puts that sentence in the response so no surface
 * can quietly ship a weaker one. This renders `message` verbatim rather than paraphrasing it,
 * and keys the lead-in off `sponsored`, which is a fact about the ranking and not a style
 * choice.
 *
 * The canvas writes it as a **bold brass lead-in followed by the rest of the sentence** —
 * "Sponsored placement. Founders bid for these positions…" — not as a badge beside a line of
 * text. The build had a pill there, which made the most important sentence on the page look
 * like a label attached to something else.
 *
 * That lead-in is not written here: it is core's own first sentence, emphasised. Every one of
 * core's messages opens with the claim and continues with the reason — "Sponsored placement."
 * then "Founders bid…", "Ranked by qualified buyer clicks during this period." then "Bid
 * amount does not affect this ranking." So the split is presentational and the wording stays
 * verbatim to the character, which is what `contracts/API.md` requires. A hard-coded lead-in
 * would print core's sentence twice — which is exactly what it did before this was fixed.
 */

/** Core's message as its opening claim and the rest, for the canvas's two-weight treatment. */
function splitLead(message: string): [string, string] {
  const end = message.indexOf(". ");
  if (end === -1) return [message, ""];
  return [message.slice(0, end + 1), message.slice(end + 2)];
}

export function DisclosureNote({
  disclosure,
  prominent = false,
}: {
  disclosure: RankingDisclosure;
  /**
   * Render as the board's own top border rather than as a note above it: a brass band
   * between the heading and rank 01, at body size. When the board moved above the fold the
   * disclosure had to move with it, and reclaiming that space *from the disclosure* is the
   * one trade canon § 12 does not allow.
   */
  prominent?: boolean | undefined;
}) {
  const [lead, rest] = splitLead(disclosure.message);

  return (
    <p className={`${styles.disclosure} ${prominent ? styles.prominent : ""}`.trim()}>
      <strong className={styles.lead}>{lead}</strong>
      {rest === "" ? null : <> <span className={styles.message}>{rest}</span></>}
    </p>
  );
}
